import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { transformProduct } from "../helpers/productApiHelpers/transformProduct";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";

import type { FetchBaseQueryMeta } from "@reduxjs/toolkit/query";

// Import the RAW types for API responses
import type { RawProduct, RawProductsResponse } from "../../../../types/index";

// Import the final TRANSFORMED types and parameter types
import type {
  GetProductsParams,
  GetProductsResult,
  ProductsCollectionData,
  TransformedProduct,
} from "../types/productApiTypes";

// --- API Slice Definition ---

const WC_STORE_API_BASE =
  window.commonVariables?.wcStoreApiBase || "/wp-json/wc/store/v1";

const MINOR_UNIT_DEFAULT = 2;

export const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetches products list. Uses RawProductsResponse, transforms to GetProductsResult.
     */
    getProducts: builder.query<GetProductsResult, GetProductsParams>({
      query: (params: GetProductsParams = {}) => {
        const queryParams: Record<string, any> = {};
        const multiplier = Math.pow(10, MINOR_UNIT_DEFAULT);

        if (params.category && params.category.length)
          queryParams["category"] = params.category.join(",");
        if (params.tag && params.tag.length)
          queryParams["tag"] = params.tag.join(",");
        if (params.brand && params.brand.length)
          queryParams["brand"] = params.brand.join(",");
        if (params.search) queryParams["search"] = params.search;

        if (params.sku) queryParams["sku"] = params.sku;

        if (params.attributes && Object.keys(params.attributes).length > 0) {
          let i = 0;
          for (const taxonomy in params.attributes) {
            const termSlugs = params.attributes[taxonomy];
            if (termSlugs.length > 0) {
              queryParams[`attributes[${i}][attribute]`] = taxonomy;
              queryParams[`attributes[${i}][slug]`] = termSlugs.join(",");
              i++;
            }
          }
          if (Object.keys(params.attributes).length > 1) {
            queryParams["attribute_relation"] = "and";
          }
        }

        if (params.orderby) {
          switch (params.orderby) {
            case "menu_order":
              queryParams["orderby"] = "menu_order";
              queryParams["order"] = "asc";
              break;
            case "popularity":
              queryParams["orderby"] = "popularity";
              break;
            case "date":
              queryParams["orderby"] = "date";
              break;
            case "price-asc":
              queryParams["orderby"] = "price";
              queryParams["order"] = "asc";
              break;
            case "price-desc":
              queryParams["orderby"] = "price";
              queryParams["order"] = "desc";
              break;
            default:
              queryParams["orderby"] = "menu_order";
              queryParams["order"] = "asc";
          }
        }
        if (typeof params.minPrice === "number") {
          const minPriceMinor = Math.round(params.minPrice * multiplier);
          queryParams["min_price"] = String(minPriceMinor);
        }
        if (typeof params.maxPrice === "number") {
          const maxPriceMinor = Math.round(params.maxPrice * multiplier);
          queryParams["max_price"] = String(maxPriceMinor);
        }
        if (params.onSale) queryParams["on_sale"] = "true";

        if (params.inStock) {
          queryParams["stock_status"] = "instock,onbackorder";
        }

        if (params.featured) queryParams["featured"] = "true";
        if (params.page) queryParams["page"] = String(params.page);
        if (params.limit) queryParams["per_page"] = String(params.limit);

        return {
          url: `${WC_STORE_API_BASE}/products`,
          params: queryParams,
        };
      },
      transformResponse: (
        response: RawProductsResponse,
        meta: FetchBaseQueryMeta | undefined,
      ): GetProductsResult => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProducts expected an array but received:",
            response,
          );
          return {
            products: [],
            pagination: { totalProducts: 0, totalPages: 1 },
          };
        }
        const rawProducts: RawProduct[] = response;
        const headers = meta?.response?.headers;
        const totalProducts = parseInt(headers?.get("X-WP-Total") || "0", 10);
        const totalPages = parseInt(headers?.get("X-WP-TotalPages") || "1", 10);

        // --- GATHER DEPENDENCIES HERE ---
        const languageInfo = selectLanguageInfo(store.getState());

        const transformedProducts = rawProducts
          .map((p) => transformProduct(p, languageInfo))
          .filter((p): p is TransformedProduct => p !== null);

        return {
          products: transformedProducts,
          pagination: { totalProducts, totalPages },
        };
      },
      providesTags: (result) =>
        result?.products
          ? [
              ...result.products.map(({ id }) => ({
                type: "Product" as const,
                id,
              })),
              { type: "Product" as const, id: "LIST" },
            ]
          : [{ type: "Product" as const, id: "LIST" }],
    }),

    /**
     * Fetches a list of products by their IDs.
     */
    getProductsByIds: builder.query<TransformedProduct[], (number | string)[]>({
      query: (productIds) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: {
          include: productIds ? productIds.join(",") : "",
          per_page: productIds.length > 0 ? productIds.length : 0,
        },
      }),
      transformResponse: (
        response: RawProductsResponse,
      ): TransformedProduct[] => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProductsByIds expected an array but received:",
            response,
          );
          return [];
        }
        const products: RawProduct[] = response;

        // --- GATHER DEPENDENCIES HERE ---
        const languageInfo = selectLanguageInfo(store.getState());

        return products
          .map((p) => transformProduct(p, languageInfo))
          .filter((p): p is TransformedProduct => p !== null);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Product" as const, id })),
              { type: "Product" as const, id: "LIST" },
            ]
          : [{ type: "Product" as const, id: "LIST" }],
    }),

    /**
     * Fetches single product by ID. Uses RawProduct, transforms to TransformedProduct.
     */
    getProductById: builder.query<TransformedProduct | null, number | string>({
      query: (id) => ({ url: `${WC_STORE_API_BASE}/products/${id}` }),
      transformResponse: (product: RawProduct): TransformedProduct | null => {
        const languageInfo = selectLanguageInfo(store.getState());
        return transformProduct(product, languageInfo);
      },
      providesTags: (result, error, id) => [{ type: "Product" as const, id }],
    }),

    /**
     * Fetches single product by Slug. Uses RawProductsResponse, transforms first item.
     */
    getProductBySlug: builder.query<TransformedProduct | null, string>({
      query: (slug) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: { slug },
      }),
      transformResponse: (
        response: RawProductsResponse,
      ): TransformedProduct | null => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProductBySlug expected an array but received:",
            response,
          );
          return null;
        }
        const products: RawProduct[] = response;
        if (products.length === 0) return null;

        const languageInfo = selectLanguageInfo(store.getState());
        return transformProduct(products[0], languageInfo);
      },
      providesTags: (result) =>
        result ? [{ type: "Product" as const, id: result.id }] : [],
    }),

    /**
     * Fetches variations for a product. Uses RawProductsResponse, transforms to TransformedProduct[].
     */
    getProductVariations: builder.query<TransformedProduct[], number | string>({
      query: (productId) => ({
        url: `${WC_STORE_API_BASE}/products`,
        params: { type: "variation", parent: productId, per_page: 100 },
      }),
      transformResponse: (
        response: RawProductsResponse,
      ): TransformedProduct[] => {
        if (!Array.isArray(response)) {
          console.error(
            "[API Error] getProductVariations expected an array but received:",
            response,
          );
          return [];
        }
        const variations: RawProduct[] = response;
        const languageInfo = selectLanguageInfo(store.getState());

        return variations
          .map((variation) => transformProduct(variation, languageInfo))
          .filter((v): v is TransformedProduct => v !== null);
      },
      providesTags: (result, error, productId) => [
        { type: "Variation" as const, id: productId },
      ],
    }),

    /**
     * Fetches aggregate data about the product collection, like min/max price.
     */
    getProductsCollectionData: builder.query<
      ProductsCollectionData,
      GetProductsParams
    >({
      query: (params: GetProductsParams = {}) => {
        const queryParams: Record<string, any> = {
          calculate_price_range: "true",
        };

        if (params.category && params.category.length)
          queryParams["category"] = params.category.join(",");
        if (params.tag && params.tag.length)
          queryParams["tag"] = params.tag.join(",");
        if (params.brand && params.brand.length)
          queryParams["brand"] = params.brand.join(",");
        if (params.search) queryParams["search"] = params.search;
        if (params.inStock) {
          queryParams["stock_status"] = "instock,onbackorder";
        }

        return {
          url: `${WC_STORE_API_BASE}/products/collection-data`,
          params: queryParams,
        };
      },
      transformResponse: (response: {
        price_range: {
          min_price: string;
          max_price: string;
          currency_minor_unit: number;
          currency_symbol: string;
        } | null;
      }): ProductsCollectionData => {
        if (!response.price_range) {
          return {
            minPrice: 0,
            maxPrice: 1000,
            currencySymbol: "₺",
          };
        }

        const currencyMinorUnit = response.price_range.currency_minor_unit ?? 2;
        const divisor = Math.pow(10, currencyMinorUnit);

        return {
          minPrice: Math.floor(
            parseFloat(response.price_range.min_price) / divisor,
          ),
          maxPrice: Math.ceil(
            parseFloat(response.price_range.max_price) / divisor,
          ),
          currencySymbol: response.price_range.currency_symbol || "₺",
        };
      },
      providesTags: ["Product"],
    }),
  }),
  overrideExisting: false,
});

// --- Exports ---
export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useGetProductBySlugQuery,
  useGetProductsCollectionDataQuery,
  useGetProductVariationsQuery,
  useGetProductsByIdsQuery,
  util: { getRunningQueriesThunk },
} = productApi;

export const {
  getProducts,
  getProductById,
  getProductBySlug,
  getProductsCollectionData,
  getProductVariations,
  getProductsByIds,
} = productApi.endpoints;